/** Presentation helpers for the ISO-8601 stamps the API returns. */

export function timeAgo(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return iso;
  const seconds = Math.max(0, Math.round((now.getTime() - then.getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function absoluteTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** `2026-09-17` → `09-17`, the axis label the volume chart expects. */
export function shortDay(isoDate: string): string {
  return isoDate.length >= 10 ? isoDate.slice(5, 10) : isoDate;
}

const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  triaged: 'Triaged',
  in_progress: 'In progress',
  resolved: 'Resolved',
  false_positive: 'False positive',
};

export function statusLabel(status: string | undefined | null): string {
  if (!status) return '—';
  return STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
}

const SOURCE_LABELS: Record<string, string> = {
  defender: 'Defender XDR',
  sentinelone: 'SentinelOne',
  // Named by what each does (2026-09-29): one acts on users, one reads logs.
  entra: 'Entra ID user response',
  entra_logs: 'Entra ID sign-in & audit logs',
  ninjaone: 'NinjaOne',
  connectsecure: 'ConnectSecure',
  generic: 'Generic JSON ingress',
  latticeai_dlp: 'LatticeAI DLP',
  bluesphere: 'BlueSphere',
};

const FEED_LABELS: Record<string, string> = {
  entra_signin: 'Sign-ins',
  entra_signin_noninteractive: 'Non-interactive sign-ins',
  entra_audit: 'Directory audits',
  m365_audit: 'Microsoft 365 audit',
  generic: 'Generic',
  latticeai_dlp: 'LatticeAI DLP',
  cloudtrail: 'AWS CloudTrail',
};

export function feedLabel(feed: string): string {
  return FEED_LABELS[feed] ?? feed;
}

export function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source;
}

/** Seconds → `4m 12s`, `2h 05m`, `3d 4h`; `—` when undefined (e.g. TTC while open). */
export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—';
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${String(m % 60).padStart(2, '0')}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

const OUTCOME_LABELS: Record<string, string> = {
  raise_actionable: 'Raised · actionable',
  raise_suspicious: 'Raised · suspicious',
  watch: 'Watching',
  resolve_as_indicator: 'Resolved as indicator',
  resolve: 'Resolved',
  skip: 'Skipped',
};

export function outcomeLabel(outcome: string | undefined | null): string {
  if (!outcome) return '—';
  return OUTCOME_LABELS[outcome] ?? outcome.replace(/_/g, ' ');
}

export function dispositionLabel(disposition: string | undefined | null): string {
  if (!disposition) return '—';
  return disposition.charAt(0).toUpperCase() + disposition.slice(1);
}

const EVENT_LABELS: Record<string, string> = {
  raised: 'Alert raised',
  member_joined: 'Detection joined',
  reopened: 'Reopened',
  transition: 'Status changed',
  comment: 'Comment',
  closed_by_provider: 'Closed by provider',
  member_provider_closed: 'Member closed at provider',
  // Response actions (Story 19). The action itself is on the event.
  action_requested: 'Response requested',
  action_test: 'Response recorded in test mode',
  action_withheld: 'Response withheld',
  action_succeeded: 'Response succeeded',
  action_failed: 'Response failed',
  action_partial: 'Response partially succeeded',
  release_requested: 'Release requested',
  release_succeeded: 'Release succeeded',
  release_failed: 'Release failed',
  // Automatic response (task 140): a rule's request withheld for a person.
  automation_escalated: 'Automatic response withheld — an operator must decide',
  // Outreach (Story 35).
  outreach_asked: 'Asked the user',
  outreach_answered: 'Outreach answer',
  outreach_confirm_requested: 'Asked to confirm',
  outreach_contact_asked: 'Asked the security contact',
  outreach_contact_told: 'Told the security contact',
  outreach_extended: 'Waiting for the confirmation',
  outreach_ignored: 'Outreach reply not counted',
  outreach_skipped: 'Outreach skipped',
  outreach_concluded: 'Outreach concluded',
};

// ---------- Outreach timeline detail (Story 35; design r24 deviation 26) ----------

const OUTREACH_RESULT: Record<string, string> = {
  no: 'Said it was not them',
  yes: 'Confirmed it was them',
  unsure: 'Not sure',
  timeout: 'No confirmed answer in time',
  unconfirmed: 'The yes could not be confirmed',
  superseded: 'Closed before an answer',
  skipped: 'Nobody was asked',
};

const OUTREACH_OUTCOME: Record<string, string> = {
  actionable: 'promoted to actionable',
  resolve: 'resolved',
  escalate: 'left open for an analyst',
  none: 'no action',
};

const OUTREACH_ANSWER: Record<string, string> = { yes: 'yes', no: 'no', unsure: 'not sure' };

const CHANNEL: Record<string, string> = {
  sms: 'Text',
  email: 'Email',
  voice: 'Call',
  whatsapp: 'WhatsApp',
  slack: 'Slack',
  teams: 'Teams',
};

function roleLabel(role: unknown): string {
  if (role === 'contact') return 'The security contact';
  return typeof role === 'string' && role ? `The ${role}` : 'A party';
}

function channelLabel(channel: unknown): string {
  return typeof channel === 'string' ? (CHANNEL[channel] ?? channel) : 'A message';
}

function clock(iso: unknown): string | null {
  if (typeof iso !== 'string') return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.toISOString().slice(11, 16)} UTC`;
}

/**
 * What an outreach timeline entry carries, as lines under its label: who
 * answered what and how, where each send went and whether it went, the moved
 * deadline, and how the exchange ended. Only fields the outreach Lambda
 * writes (services/outreach engine `_timeline`); the reason and any notes
 * follow, as before.
 */
export function outreachDetail(event: Record<string, unknown>): string[] {
  const lines: string[] = [];
  const name = event.event;
  if (name === 'outreach_answered' || name === 'outreach_ignored') {
    const answer = typeof event.answer === 'string' ? OUTREACH_ANSWER[event.answer] : undefined;
    const via = typeof event.via === 'string' ? ` by ${channelLabel(event.via).toLowerCase()}` : '';
    if (answer) {
      const tail =
        answer === 'yes' && event.confirmed === false
          ? ' · not yet confirmed'
          : answer === 'yes' && event.confirmed === true
            ? ' · confirmed'
            : '';
      lines.push(`${roleLabel(event.role)} answered ${answer}${via}${tail}`);
    } else if (event.role || via) {
      lines.push(`${roleLabel(event.role)}${via ? `, replying${via}` : ''}`);
    }
  }
  if (name === 'outreach_contact_told' && typeof event.result === 'string') {
    lines.push(`Told: ${(OUTREACH_RESULT[event.result] ?? event.result).toLowerCase()}`);
  }
  if (Array.isArray(event.deliveries)) {
    for (const raw of event.deliveries) {
      if (!raw || typeof raw !== 'object') continue;
      const d = raw as {
        role?: unknown;
        channel?: unknown;
        ok?: unknown;
        to?: unknown;
        error?: unknown;
      };
      // Each send names its party (task 139f); older entries named it once.
      const role = d.role ?? event.role;
      const to = role ? ` (${String(role)})` : '';
      const where = typeof d.to === 'string' && d.to ? ` to ${d.to}` : '';
      const head = `${channelLabel(d.channel)}${where}${to}`;
      lines.push(
        d.ok === true ? `${head} · sent` : `${head} · failed${d.error ? `: ${String(d.error)}` : ''}`,
      );
    }
  }
  const deadline = clock(event.deadline);
  if (deadline) {
    lines.push(name === 'outreach_extended' ? `New deadline ${deadline}` : `Answer by ${deadline}`);
  }
  if (name === 'outreach_concluded' && typeof event.result === 'string') {
    const result = OUTREACH_RESULT[event.result] ?? event.result;
    const outcome =
      typeof event.outcome === 'string' ? (OUTREACH_OUTCOME[event.outcome] ?? event.outcome) : '';
    lines.push(outcome ? `${result} → ${outcome}` : result);
  }
  for (const s of [event.reason, ...(Array.isArray(event.notes) ? event.notes : [])]) {
    if (typeof s === 'string' && s.length > 0) lines.push(s);
  }
  return lines;
}

/**
 * A response action or rail name as words. Derived, never a map keyed by
 * action name: the SPA does not know the action set (the lint that keeps
 * every action name inside the response layer scans this tree).
 */
export function actionLabel(name: string | undefined | null): string {
  if (!name) return '—';
  const words = name.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function eventLabel(event: string | undefined | null): string {
  if (!event) return 'Updated';
  return EVENT_LABELS[event] ?? event.replace(/_/g, ' ');
}

/**
 * Absent-data wording shared with the notify Lambda
 * (services/notification/.../compose.py): both surfaces lead with the same
 * clause; a test on the Python side pins the two together.
 */
export const AI_UNAVAILABLE_PREFIX = 'AI summary unavailable:';
export const AI_UNAVAILABLE = `${AI_UNAVAILABLE_PREFIX} enrichment did not complete for this detection.`;
export const AI_PENDING = 'Summarizing with Claude… this usually takes a few seconds.';
